// NestFilterChip — first-class "Filter by nest" affordance (ctsk-056).
//
// Renders a row of compact uppercase code chips (e.g. [WG] [BI] [SITE])
// for every nest installed in the active org. Selection state is owned
// by the parent (so the chip integrates with the page's existing filter
// reducer); the chip just emits the slug of the active nest, and the
// parent does the work of translating `nest/<slug>` into a tag-id and
// adding it to its query.
//
// Visual model mirrors the existing quick-filter chips on Documents
// (ctsk-053) and the segmented controls on WorkItems (#99/#112): one
// "All" button at the head, then one chip per nest. Clicking the
// active chip de-selects (toggle), matching the rest of the chip rows.

import { useQuery } from '@tanstack/react-query';
import type { Nest } from '@captain-tasko/shared';
import { apiList } from '../lib/api.js';

export interface NestFilterChipProps {
  /** Currently selected nest slug, or null when no nest filter is active. */
  value: string | null;
  /** Called with the next slug (or null to clear the filter). */
  onChange: (slug: string | null) => void;
  /** Optional label shown to the left of the chip row. Defaults to "Nest:". */
  label?: string;
  /** When true, the chip row hides itself entirely if the org has no nests.
   *  Useful in toolbars that already feel crowded. */
  hideWhenEmpty?: boolean;
}

export function NestFilterChip({
  value,
  onChange,
  label = 'Nest:',
  hideWhenEmpty = true,
}: NestFilterChipProps) {
  const nestsQ = useQuery({
    queryKey: ['nests'],
    queryFn: () => apiList<Nest>('/nests'),
    // Nests change infrequently — cache aggressively.
    staleTime: 60_000,
  });

  const nests = nestsQ.data?.data ?? [];
  if (nests.length === 0 && hideWhenEmpty) return null;

  return (
    <div
      style={{
        display: 'inline-flex',
        gap: 6,
        alignItems: 'center',
        fontSize: 12,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <button
        type="button"
        onClick={() => onChange(null)}
        aria-pressed={value === null}
        style={chipStyle(value === null)}
        title="Show items from every nest"
      >
        All
      </button>
      {nests.map((n) => {
        const active = value === n.slug;
        return (
          <button
            key={n.id}
            type="button"
            onClick={() => onChange(active ? null : n.slug)}
            aria-pressed={active}
            // Title carries the human name — code-only chip stays compact.
            title={`${n.name}${n.description ? ` — ${n.description}` : ''}`}
            style={chipStyle(active)}
          >
            {n.code}
          </button>
        );
      })}
    </div>
  );
}

function chipStyle(active: boolean): React.CSSProperties {
  return {
    padding: '3px 8px',
    fontSize: 11,
    fontFamily: 'var(--font-mono)',
    fontWeight: active ? 700 : 500,
    borderRadius: 4,
    letterSpacing: '.04em',
    border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-strong)'}`,
    background: active
      ? 'var(--accent-primary)'
      : 'var(--surface-raised)',
    color: active ? '#fff' : 'var(--text-primary)',
    cursor: 'pointer',
  };
}
